import React, { useState, useEffect } from 'react';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, convertToRaw, convertFromRaw, ContentState, RichUtils } from 'draft-js';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import 'draft-js/dist/Draft.css';

interface RichTextEditorProps {
  value?: string;
  onChange: (content: string) => void;
  darkMode?: boolean;
  placeholder?: string;
}

const RichTextEditorSimple: React.FC<RichTextEditorProps> = ({
  value = '',
  onChange,
  darkMode = false,
  placeholder = 'Write your blog post content here...'
}) => {
  console.log('DEBUG RichTextEditorSimple: Component rendered with value:', value ? `${value.length} chars` : 'EMPTY');
  console.log('DEBUG RichTextEditorSimple: Value preview:', value ? value.substring(0, 200) + '...' : 'EMPTY');
  // Helper function to create initial editor state
  const createInitialEditorState = (initialValue: string) => {
    if (!initialValue) {
      return EditorState.createEmpty();
    }
    
    try {
      // Try to parse as rich text JSON first
      const parsedContent = JSON.parse(initialValue);
      
      // Check if it's a Contentful rich text document
      if (parsedContent.nodeType === 'document') {
        console.log('DEBUG RichTextEditorSimple: Initial content is Contentful rich text, converting to DraftJS');
        // Convert Contentful rich text to DraftJS format
        const draftContent = convertContentfulToDraft(parsedContent);
        const contentState = convertFromRaw(draftContent);
        return EditorState.createWithContent(contentState);
      } else {
        // Assume it's already DraftJS format
        const contentState = convertFromRaw(parsedContent);
        return EditorState.createWithContent(contentState);
      }
    } catch {
      // If not JSON, treat as plain text
      const contentState = ContentState.createFromText(initialValue);
      return EditorState.createWithContent(contentState);
    }
  };

  const [editorState, setEditorState] = useState(() => createInitialEditorState(value || ''));
  const [isMounted, setIsMounted] = useState(false);
  const [lastLoadedValue, setLastLoadedValue] = useState(value || '');

  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  // Only update editor state when value changes from external source (not from typing)
  // This handles the case where component mounts empty, then content loads asynchronously
  useEffect(() => {
    if (isMounted && value !== lastLoadedValue) {
      // Check if this is a significant change (not just typing)
      const currentText = editorState.getCurrentContent().getPlainText();
      
      console.log('DEBUG RichTextEditorSimple: Value change detected');
      console.log('  - currentText.length:', currentText.length);
      console.log('  - value.length:', value ? value.length : 0);
      console.log('  - lastLoadedValue.length:', lastLoadedValue.length);
      
      // More robust check for Contentful document
      let isContentfulDocument = false;
      if (value) {
        try {
          const parsed = JSON.parse(value);
          isContentfulDocument = parsed && parsed.nodeType === 'document';
        } catch {
          isContentfulDocument = false;
        }
      }
      
      console.log('  - is Contentful document:', isContentfulDocument);
      
      // Check if this is edit mode content (Contentful document from external source)
      // Only load if:
      // 1. Value is a Contentful document
      // 2. Current editor is mostly empty (less than 50 chars)
      // 3. This is likely initial load (lastLoadedValue is empty or small)
      if (value && 
          isContentfulDocument &&
          currentText.length < 50 &&
          lastLoadedValue.length < 100) {
        console.log('DEBUG RichTextEditorSimple: ✅ Loading content for edit mode (async)');
        const newEditorState = createInitialEditorState(value);
        setEditorState(newEditorState);
        setLastLoadedValue(value);
      }
      // If value becomes empty (new post), reset editor
      else if (!value && lastLoadedValue) {
        console.log('DEBUG RichTextEditorSimple: Resetting to empty for new post');
        setEditorState(EditorState.createEmpty());
        setLastLoadedValue('');
      } else {
        console.log('DEBUG RichTextEditorSimple: ❌ Conditions not met, not loading content');
      }
    }
  }, [value, isMounted, lastLoadedValue]);

  const handleEditorStateChange = (newEditorState: EditorState) => {
    if (!isMounted) return;
    
    setEditorState(newEditorState);
    
    // Convert editor content to Contentful rich text format
    const contentState = newEditorState.getCurrentContent();
    const rawContentState = convertToRaw(contentState);
    
    console.log('DEBUG handleEditorStateChange: Raw content state:', rawContentState);
    console.log('DEBUG handleEditorStateChange: Entity map:', rawContentState.entityMap);
    
    // Convert DraftJS format to Contentful Rich Text format
    const contentfulRichText = convertDraftToContentful(rawContentState);
    
    console.log('DEBUG handleEditorStateChange: Final Contentful rich text:', contentfulRichText);
    
    // Send back as JSON string for the backend
    onChange(JSON.stringify(contentfulRichText));
  };

  // Convert DraftJS raw state to Contentful Rich Text format
  // Convert Contentful rich text format back to DraftJS format for editing
  const convertContentfulToDraft = (contentfulDoc: any) => {
    console.log('DEBUG convertContentfulToDraft: Converting Contentful to DraftJS:', contentfulDoc);
    
    const blocks: any[] = [];
    let blockKey = 0;
    let entityMap: any = {};
    let entityCounter = 0;

    const processContentfulNode = (node: any): any => {
      switch (node.nodeType) {
        case 'document':
          // Process all content nodes
          node.content?.forEach((childNode: any) => {
            const block = processContentfulNode(childNode);
            if (block) {
              if (Array.isArray(block)) {
                blocks.push(...block);
              } else {
                blocks.push(block);
              }
            }
          });
          break;

        case 'paragraph':
          return createBlock('unstyled', node);

        case 'heading-1':
          return createBlock('header-one', node);

        case 'heading-2':
          return createBlock('header-two', node);

        case 'heading-3':
          return createBlock('header-three', node);

        case 'blockquote':
          return createBlock('blockquote', node);

        case 'unordered-list':
          const unorderedItems: any[] = [];
          node.content?.forEach((listItem: any) => {
            if (listItem.nodeType === 'list-item') {
              // Extract text from the first paragraph in the list item
              const paragraph = listItem.content?.[0];
              if (paragraph && paragraph.nodeType === 'paragraph') {
                unorderedItems.push(createBlock('unordered-list-item', paragraph));
              }
            }
          });
          return unorderedItems;

        case 'ordered-list':
          const orderedItems: any[] = [];
          node.content?.forEach((listItem: any) => {
            if (listItem.nodeType === 'list-item') {
              // Extract text from the first paragraph in the list item
              const paragraph = listItem.content?.[0];
              if (paragraph && paragraph.nodeType === 'paragraph') {
                orderedItems.push(createBlock('ordered-list-item', paragraph));
              }
            }
          });
          return orderedItems;

        default:
          console.log('DEBUG convertContentfulToDraft: Unknown node type:', node.nodeType);
          return null;
      }
    };

    const createBlock = (blockType: string, node: any) => {
      const blockData = {
        key: `block_${blockKey++}`,
        type: blockType,
        text: '',
        depth: 0,
        inlineStyleRanges: [] as any[],
        entityRanges: [] as any[],
        data: {}
      };

      let currentOffset = 0;

      // Process text content and inline formatting
      node.content?.forEach((textNode: any) => {
        if (textNode.nodeType === 'text') {
          const text = textNode.value || '';
          console.log('DEBUG convertContentfulToDraft: Processing text:', JSON.stringify(text));
          blockData.text += text;

          // Process marks (formatting)
          if (textNode.marks && textNode.marks.length > 0) {
            textNode.marks.forEach((mark: any) => {
              let style = '';
              switch (mark.type) {
                case 'bold':
                  style = 'BOLD';
                  break;
                case 'italic':
                  style = 'ITALIC';
                  break;
                case 'underline':
                  style = 'UNDERLINE';
                  break;
                case 'strikethrough':
                  style = 'STRIKETHROUGH';
                  break;
              }
              
              if (style) {
                blockData.inlineStyleRanges.push({
                  offset: currentOffset,
                  length: text.length,
                  style: style
                });
              }
            });
          }

          currentOffset += text.length;
        } else if (textNode.nodeType === 'hyperlink') {
          // Handle hyperlinks
          const linkText = textNode.content?.[0]?.value || '';
          const linkUrl = textNode.data?.uri || '';

          // Create entity for the link
          const entityKey = `entity_${entityCounter++}`;
          entityMap[entityKey] = {
            type: 'LINK',
            mutability: 'MUTABLE',
            data: {
              url: linkUrl
            }
          };

          // Add the link text
          blockData.text += linkText;

          // Add entity range
          blockData.entityRanges.push({
            offset: currentOffset,
            length: linkText.length,
            key: parseInt(entityKey.split('_')[1])
          });

          // Process marks for the link text
          const linkTextNode = textNode.content?.[0];
          if (linkTextNode?.marks && linkTextNode.marks.length > 0) {
            linkTextNode.marks.forEach((mark: any) => {
              let style = '';
              switch (mark.type) {
                case 'bold':
                  style = 'BOLD';
                  break;
                case 'italic':
                  style = 'ITALIC';
                  break;
                case 'underline':
                  style = 'UNDERLINE';
                  break;
                case 'strikethrough':
                  style = 'STRIKETHROUGH';
                  break;
              }
              
              if (style) {
                blockData.inlineStyleRanges.push({
                  offset: currentOffset,
                  length: linkText.length,
                  style: style
                });
              }
            });
          }

          currentOffset += linkText.length;
        }
      });

      return blockData;
    };

    // Process the document
    processContentfulNode(contentfulDoc);

    // If no blocks were created, create an empty block
    if (blocks.length === 0) {
      blocks.push({
        key: 'block_0',
        type: 'unstyled',
        text: '',
        depth: 0,
        inlineStyleRanges: [],
        entityRanges: [],
        data: {}
      });
    }

    const draftContent = {
      blocks: blocks,
      entityMap: entityMap
    };

    console.log('DEBUG convertContentfulToDraft: Final DraftJS content:', draftContent);
    return draftContent;
  };

  const convertDraftToContentful = (rawContentState: any) => {
    console.log('DEBUG convertDraftToContentful: Full raw content state:', rawContentState);
    console.log('DEBUG convertDraftToContentful: Entity map:', rawContentState.entityMap);
    
    const contentfulDocument = {
      nodeType: 'document',
      data: {},
      content: [] as any[]
    };

    // Group list items together
    let currentList: any = null;
    let currentListType: string | null = null;

    rawContentState.blocks.forEach((block: any, blockIndex: number) => {
      console.log(`DEBUG convertDraftToContentful: Processing block ${blockIndex}:`, block);
      console.log(`DEBUG convertDraftToContentful: Block has ${block.entityRanges ? block.entityRanges.length : 0} entity ranges`);
      
      let contentfulBlock: any;

      // Process inline styles and entities for text formatting
      const processInlineText = (text: string, inlineStyleRanges: any[], entityRanges: any[]) => {
        if (!text) return [];
        
        
        // Create a map of character positions to styles
        const charStyles: { [key: number]: string[] } = {};
        
        // Apply inline style ranges
        if (inlineStyleRanges) {
          inlineStyleRanges.forEach((range: any) => {
            for (let i = range.offset; i < range.offset + range.length; i++) {
              if (!charStyles[i]) charStyles[i] = [];
              charStyles[i].push(range.style);
            }
          });
        }
        
        // Handle entities (links) first, then apply styles
        if (entityRanges && entityRanges.length > 0) {
          const content: any[] = [];
          let lastOffset = 0;
          
          entityRanges.forEach((entityRange: any) => {
            const entity = rawContentState.entityMap[entityRange.key];
            
            // Add text before link with proper styling
            if (entityRange.offset > lastOffset) {
              const beforeText = text.substring(lastOffset, entityRange.offset);
              content.push(...createStyledTextNodes(beforeText, lastOffset, charStyles));
            }
            
            // Add link
            if (entity && entity.type === 'LINK') {
              const linkText = text.substring(entityRange.offset, entityRange.offset + entityRange.length);
              // Get styles for the link text
              const linkMarks = getMarksForRange(entityRange.offset, entityRange.length, charStyles);
              
              console.log('DEBUG convertDraftToContentful: Processing link entity:', entity);
              console.log('DEBUG convertDraftToContentful: Link URL:', entity.data.url);
              console.log('DEBUG convertDraftToContentful: Link text:', linkText);
              
              content.push({
                nodeType: 'hyperlink',
                data: {
                  uri: entity.data.url
                },
                content: [{
                  nodeType: 'text',
                  value: linkText,
                  marks: linkMarks,
                  data: {}
                }]
              });
            }
            
            lastOffset = entityRange.offset + entityRange.length;
          });
          
          // Add remaining text with proper styling
          if (lastOffset < text.length) {
            const remainingText = text.substring(lastOffset);
            content.push(...createStyledTextNodes(remainingText, lastOffset, charStyles));
          }
          
          return content;
        }
        
        // No entities, just create styled text nodes
        return createStyledTextNodes(text, 0, charStyles);
      };
      
      // Helper function to create styled text nodes
      const createStyledTextNodes = (text: string, offset: number, charStyles: { [key: number]: string[] }) => {
        if (!text) return [];
        
        const nodes: any[] = [];
        let currentOffset = offset;
        let currentText = '';
        let currentStyles: string[] = [];
        
        for (let i = 0; i < text.length; i++) {
          const char = text[i];
          const charPosition = offset + i;
          const stylesAtPosition = charStyles[charPosition] || [];
          
          // Check if styles have changed
          const stylesChanged = !arraysEqual(currentStyles, stylesAtPosition);
          
          if (stylesChanged && currentText) {
            // Create node for accumulated text
            nodes.push({
              nodeType: 'text',
              value: currentText,
              marks: stylesToMarks(currentStyles),
              data: {}
            });
            currentText = '';
          }
          
          currentText += char;
          currentStyles = stylesAtPosition;
        }
        
        // Add final text node
        if (currentText) {
          nodes.push({
            nodeType: 'text',
            value: currentText,
            marks: stylesToMarks(currentStyles),
            data: {}
          });
        }
        
        return nodes.length > 0 ? nodes : [{
          nodeType: 'text',
          value: text,
          marks: [],
          data: {}
        }];
      };
      
      // Helper function to get marks for a range
      const getMarksForRange = (offset: number, length: number, charStyles: { [key: number]: string[] }) => {
        const allStyles = new Set<string>();
        for (let i = offset; i < offset + length; i++) {
          if (charStyles[i]) {
            charStyles[i].forEach(style => allStyles.add(style));
          }
        }
        return stylesToMarks(Array.from(allStyles));
      };
      
      // Helper function to convert DraftJS styles to Contentful marks
      const stylesToMarks = (styles: string[]) => {
        return styles.map(style => {
          const markType = style.toLowerCase();
          switch (markType) {
            case 'bold':
              return { type: 'bold' };
            case 'italic':
              return { type: 'italic' };
            case 'underline':
              return { type: 'underline' };
            case 'strikethrough':
              return { type: 'strikethrough' };
            default:
              return { type: markType.replace('_', '-') };
          }
        });
      };
      
      // Helper function to compare arrays
      const arraysEqual = (a: string[], b: string[]) => {
        if (a.length !== b.length) return false;
        const sortedA = [...a].sort();
        const sortedB = [...b].sort();
        return sortedA.every((val, i) => val === sortedB[i]);
      };

      switch (block.type) {
        case 'header-one':
          currentList = null;
          currentListType = null;
          contentfulBlock = {
            nodeType: 'heading-1',
            data: {},
            content: processInlineText(block.text, block.inlineStyleRanges || [], block.entityRanges || [])
          };
          break;
        case 'header-two':
          currentList = null;
          currentListType = null;
          contentfulBlock = {
            nodeType: 'heading-2',
            data: {},
            content: processInlineText(block.text, block.inlineStyleRanges || [], block.entityRanges || [])
          };
          break;
        case 'header-three':
          currentList = null;
          currentListType = null;
          contentfulBlock = {
            nodeType: 'heading-3',
            data: {},
            content: processInlineText(block.text, block.inlineStyleRanges || [], block.entityRanges || [])
          };
          break;
        case 'unordered-list-item':
          if (currentListType !== 'unordered-list') {
            currentList = {
              nodeType: 'unordered-list',
              data: {},
              content: []
            };
            currentListType = 'unordered-list';
            contentfulDocument.content.push(currentList);
          }
          
          currentList.content.push({
            nodeType: 'list-item',
            data: {},
            content: [{
              nodeType: 'paragraph',
              data: {},
              content: processInlineText(block.text, block.inlineStyleRanges || [], block.entityRanges || [])
            }]
          });
          break;
        case 'ordered-list-item':
          if (currentListType !== 'ordered-list') {
            currentList = {
              nodeType: 'ordered-list',
              data: {},
              content: []
            };
            currentListType = 'ordered-list';
            contentfulDocument.content.push(currentList);
          }
          
          currentList.content.push({
            nodeType: 'list-item',
            data: {},
            content: [{
              nodeType: 'paragraph',
              data: {},
              content: processInlineText(block.text, block.inlineStyleRanges || [], block.entityRanges || [])
            }]
          });
          break;
        case 'blockquote':
          currentList = null;
          currentListType = null;
          contentfulBlock = {
            nodeType: 'blockquote',
            data: {},
            content: [{
              nodeType: 'paragraph',
              data: {},
              content: processInlineText(block.text, block.inlineStyleRanges || [], block.entityRanges || [])
            }]
          };
          break;
        default: // 'unstyled' and others
          currentList = null;
          currentListType = null;
          if (block.text.trim()) { // Only add non-empty paragraphs
            contentfulBlock = {
              nodeType: 'paragraph',
              data: {},
              content: processInlineText(block.text, block.inlineStyleRanges || [], block.entityRanges || [])
            };
          }
      }

      if (contentfulBlock) {
        contentfulDocument.content.push(contentfulBlock);
      }
    });

    return contentfulDocument;
  };

  // Custom link functionality
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');

  const handleAddLink = () => {
    const selection = editorState.getSelection();
    if (selection.isCollapsed()) {
      alert('Please select some text first to create a link.');
      return;
    }
    setShowLinkModal(true);
  };

  const confirmLink = () => {
    if (!linkUrl.trim()) {
      alert('Please enter a valid URL.');
      return;
    }

    const finalUrl = linkUrl.startsWith('http') ? linkUrl : `https://${linkUrl}`;
    console.log('DEBUG confirmLink: Creating link with URL:', finalUrl);
    
    const contentState = editorState.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity('LINK', 'MUTABLE', {
      url: finalUrl
    });
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
    const newEditorState = EditorState.set(editorState, { currentContent: contentStateWithEntity });
    const editorStateWithLink = RichUtils.toggleLink(newEditorState, newEditorState.getSelection(), entityKey);
    
    console.log('DEBUG confirmLink: Link created, entity key:', entityKey);
    console.log('DEBUG confirmLink: Final editor state content:', convertToRaw(editorStateWithLink.getCurrentContent()));
    
    setEditorState(editorStateWithLink);
    
    // Manually trigger the change handler to ensure the link is saved
    const finalContentState = editorStateWithLink.getCurrentContent();
    const finalRawContentState = convertToRaw(finalContentState);
    const finalContentfulRichText = convertDraftToContentful(finalRawContentState);
    console.log('DEBUG confirmLink: Manually triggered conversion result:', finalContentfulRichText);
    onChange(JSON.stringify(finalContentfulRichText));
    
    setShowLinkModal(false);
    setLinkUrl('');
  };

  // Custom toolbar configuration without the broken link button
  const toolbarConfig = {
    options: ['inline', 'blockType', 'list', 'textAlign', 'history'],
    inline: {
      options: ['bold', 'italic', 'underline', 'strikethrough'],
    },
    blockType: {
      options: ['Normal', 'H1', 'H2', 'H3', 'Blockquote'],
    },
    list: {
      options: ['unordered', 'ordered'],
    },
    textAlign: {
      options: ['left', 'center', 'right'],
    },
    history: {
      options: ['undo', 'redo'],
    },
  };

  return (
    <div className="border rounded-lg">
      <style dangerouslySetInnerHTML={{
        __html: `
          .DraftEditor-root {
            direction: ltr !important;
            text-align: left !important;
          }
          .DraftEditor-editorContainer {
            direction: ltr !important;
          }
          .public-DraftEditor-content {
            direction: ltr !important;
            text-align: left !important;
          }
          .public-DraftEditorPlaceholder-root {
            direction: ltr !important;
            text-align: left !important;
          }
          [data-contents="true"] {
            direction: ltr !important;
            text-align: left !important;
          }
        `
      }} />
      {/* Custom Link Button */}
      <div className="bg-gray-50 border-b p-2 flex items-center gap-2">
        <button
          type="button"
          onClick={handleAddLink}
          className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center gap-1"
          title="Add Link"
        >
          🔗 Add Link
        </button>
        {editorState.getSelection().isCollapsed() ? (
          <span className="text-xs text-gray-500">Select text to create a link</span>
        ) : (
          <span className="text-xs text-green-600">Text selected - ready to link</span>
        )}
      </div>

      {isMounted && (
        <Editor
          editorState={editorState}
          onEditorStateChange={handleEditorStateChange}
          toolbar={toolbarConfig}
          placeholder={placeholder}
          editorStyle={{
            minHeight: '400px',
            padding: '16px',
            fontSize: '16px',
            lineHeight: '1.6',
            direction: 'ltr',
            textAlign: 'left'
          }}
          stripPastedStyles={false}
          spellCheck={true}
          readOnly={false}
          tabIndex={1}
        />
      )}

      {/* Link Modal */}
      {showLinkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full mx-4">
            <h3 className="text-lg font-medium mb-4">Add Link</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">URL:</label>
              <input
                type="url"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://example.com"
                className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowLinkModal(false);
                  setLinkUrl('');
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmLink}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Add Link
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RichTextEditorSimple;